"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ACCOUNTING_INVOICE_SYNC_IDEMPOTENCY_PREFIX = exports.ACCOUNTING_SYNC_OPERATION_CREATE_XERO_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE = exports.ACCOUNTING_SYNC_OPERATION_CREATE_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE = void 0;
exports.isCreateInvoiceAccountingSyncOp = isCreateInvoiceAccountingSyncOp;
/**
 * Push a Handshake/Nucleus project invoice to Xero as an **ACCREC** (accounts receivable) sales invoice.
 *
 * ## Triggers
 * - **Handshake**: when a `ProjectInvoice` becomes **paid** (Finmo webhook / markAsPaid), if auto-sync is enabled
 *   (default **on** when unset).
 * - **Nucleus**: when a `ProjectInvoice` is **created** (PENDING) if auto-sync is enabled (default **off** when unset);
 *   also on pay to reconcile ACCREC + allocate payment; after **material edit** of due date / amounts on an
 *   already-synced pending invoice (orchestrator `reconcile: true` so Xero DueDate/lines update).
 * - **Handshake revision accept (HAN-393)**: after the client accepts a revision / VO, best-effort ACCREC
 *   create/reconcile/void (see mapping below). Propose-only revisions do **not** sync.
 * - Manual sync endpoint (always available; Nucleus may sync unpaid).
 *
 * Create posts **ACCREC** only (**AUTHORISED**, awaiting payment in Xero). Do **not** post a payment here.
 * Payments are posted separately when escrow is released / Nucleus invoice is paid — see
 * {@link ACCOUNTING_SYNC_OPERATION_ALLOCATE_XERO_PAYMENT_FOR_HANDSHAKE_PROJECT_INVOICE} in `accounting-invoice-payment-sync.contract.ts`.
 *
 * **Idempotency**: one logical “create Xero invoice for this Handshake invoice” per `projectInvoiceId`.
 * Use `AccountingSyncIdempotency` with {@link ACCOUNTING_INVOICE_SYNC_IDEMPOTENCY_PREFIX} and a stable key (see below).
 *
 * **Out of scope** (later): `settledAt` / settlement events, credit notes for paid invoices,
 * multi-line milestone splits, tax bands, FX, contacts sync — keep sync narrow so we can ship.
 *
 * ## Revision / VO mapping (HAN-393)
 * Reuse this create op (and {@link ACCOUNTING_SYNC_OPERATION_VOID_XERO_INVOICE} for dropped unpaid ACCRECs).
 * No distinct VO document type.
 *
 * | Local outcome on revision **accept** | Accounting |
 * | --- | --- |
 * | New PENDING invoice (VO after a paid parent, milestone-level VO, or new regular invoice) | **New ACCREC** (`project_invoice:{id}`) |
 * | VO absorbed into still-PENDING original (amount/due change, same id) | **Amend same ACCREC** (`reconcile: true`) |
 * | Paid / settled original | **No change** |
 * | PENDING invoice dropped from the revision | **Void unpaid ACCREC** if previously synced |
 * | Nucleus split project | Reconcile the consolidated `project_split:{projectId}` ACCREC |
 * | Posted payment / split recognition / absorbed CC fee journals | **Leave them** |
 *
 * Voiding unpaid AUTHORISED ACCRECs on Nucleus cancel/recreate is handled by
 * {@link ACCOUNTING_SYNC_OPERATION_VOID_XERO_INVOICE} in `accounting-invoice-void-sync.contract.ts`.
 * After void, the create idempotency row for that `projectInvoiceId` is cleared so a replacement
 * row that reuses the same local id can sync a new ACCREC (recreate preserves pending ids).
 *
 * ## Xero document
 * - **Type**: `ACCREC` (sales invoice / accounts receivable).
 * - **Line items** (when Handshake fee &gt; 0):
 *   1. **Project revenue** — {@link AccountingAccountMappingSlot.DEFAULT_REVENUE} — milestone net of business fee share.
 *   2. **Handshake platform fee** — {@link AccountingAccountMappingSlot.HANDSHAKE_PLATFORM_FEE} — total fee (business + client portions).
 * - When fee is zero, a single revenue line uses `milestoneAmountCaptured`.
 * - **Currency**: Handshake `Project.currency` (see `ProjectBase.currency` in `@handshake/core-api`).
 * - **Reference / invoice number**: Handshake `invoiceCode` is set as both Xero `Reference` and
 *   `InvoiceNumber` on create (lookup remains by `Reference`). QBO uses the same code as `DocNumber`.
 *
 * ## Account mappings (Handshake → Xero)
 * | Slot | v1 usage |
 * |------|----------|
 * | {@link AccountingAccountMappingSlot.DEFAULT_REVENUE} | **Required** — revenue line on the ACCREC. |
 * | {@link AccountingAccountMappingSlot.HANDSHAKE_PLATFORM_FEE} | **Required** — fee line when total Handshake fee &gt; 0. |
 * | {@link AccountingAccountMappingSlot.HANDSHAKE_ESCROW_BANK} | **Not used** on invoice create — required for payment allocation at pay-in (Handshake) / release (Nucleus). |
 *
 * ## Orchestrator contract
 * - **Operation string**: prefer {@link ACCOUNTING_SYNC_OPERATION_CREATE_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE}
 *   (legacy Xero value still accepted — see {@link isCreateInvoiceAccountingSyncOp}).
 * - **Payload**: {@link AccountingInvoiceSyncHandshakePayload}
 * - **Idempotency key**: `${ACCOUNTING_INVOICE_SYNC_IDEMPOTENCY_PREFIX}${projectInvoiceId}` (stable per Handshake invoice).
 */
/** Provider-neutral create-invoice sync operation (preferred for new writes). */
exports.ACCOUNTING_SYNC_OPERATION_CREATE_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE = "CREATE_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE";
/**
 * Legacy Xero-branded operation string — still present in existing idempotency rows.
 * Prefer {@link ACCOUNTING_SYNC_OPERATION_CREATE_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE} for new writes.
 */
exports.ACCOUNTING_SYNC_OPERATION_CREATE_XERO_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE = "CREATE_XERO_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE";
/** True when `operation` is the neutral or legacy Xero create-invoice op. */
function isCreateInvoiceAccountingSyncOp(operation) {
    return (operation === exports.ACCOUNTING_SYNC_OPERATION_CREATE_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE ||
        operation === exports.ACCOUNTING_SYNC_OPERATION_CREATE_XERO_INVOICE_FROM_HANDSHAKE_PROJECT_INVOICE);
}
/** Prefix for idempotency keys so they don’t collide with other accounting operations. */
exports.ACCOUNTING_INVOICE_SYNC_IDEMPOTENCY_PREFIX = "project_invoice:";
